import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

// Hands SEVERAL PDFs to the system at once (Owner decision list 2026-10-19 item 28, 「先下载全部报告」): on a
// phone one share sheet with every file (Save to Files / Drive saves them together); in a browser each file
// downloads in turn. Same discipline as ./share.ts.
export interface NamedPdf {
  fileName: string;
  blob: Blob;
}

export type ShareAllCode = 'shared' | 'downloaded' | 'cancelled' | 'failed';

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

// Two files may carry the same name (two inspections at one address on one day): the second gets " (2)".
export function uniqueFileNames(names: readonly string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((name) => {
    const count = (seen.get(name) ?? 0) + 1;
    seen.set(name, count);
    if (count === 1) return name;
    const dot = name.toLowerCase().endsWith('.pdf') ? name.length - 4 : name.length;
    return `${name.slice(0, dot)} (${count})${name.slice(dot)}`;
  });
}

export async function shareReportFiles(files: readonly NamedPdf[]): Promise<{ code: ShareAllCode; detail: string }> {
  const names = uniqueFileNames(files.map((f) => f.fileName));
  if (Capacitor.isNativePlatform()) {
    try {
      const uris: string[] = [];
      for (const [index, file] of files.entries()) {
        const written = await Filesystem.writeFile({ path: `reports-all/${names[index]}`, data: await blobToBase64(file.blob), directory: Directory.Cache, recursive: true });
        uris.push(written.uri);
      }
      await Share.share({ files: uris });
      return { code: 'shared', detail: '' };
    } catch (raw) {
      const text = raw instanceof Error ? raw.message : String(raw);
      if (/cancel/i.test(text)) return { code: 'cancelled', detail: '' };
      return { code: 'failed', detail: text };
    }
  }
  try {
    for (const [index, file] of files.entries()) {
      const url = URL.createObjectURL(file.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = names[index];
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      // Browsers drop downloads started in the same instant; a short pause keeps every file.
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    return { code: 'downloaded', detail: '' };
  } catch (raw) {
    return { code: 'failed', detail: raw instanceof Error ? raw.message : String(raw) };
  }
}
